import React, { useState } from "react";
import { Box, Stack } from "@mui/material";
import { CalendarMonth } from "@mui/icons-material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestStatusTimeline } from "components/DesignSystem/RequestStatusTimeline";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import RequestDetailsSection from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { CancelLeaveDialog } from "pages/TeamZone/LeaveSummary/CancelLeave";
import { canonicalLeaveStatus } from "pages/TeamZone/Approve/Leave/LeaveStatusCards";
import { useSelector } from "react-redux";
import { format, isBefore, isValid, parse, parseISO, startOfDay } from "date-fns";
import { normalizeApprovalPersonFields } from "pages/TeamZone/Approve/displayNameUtils";

const detailAccent = {
  period: detailAccentPalette.indigo,
  type: detailAccentPalette.pink,
  days: detailAccentPalette.green,
  reason: detailAccentPalette.orange,
  policy: detailAccentPalette.purple,
};

const parseKnownDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;

  const text = String(value).trim();
  // date-fns's parse() ignores unmatched trailing text instead of failing,
  // so a date-only pattern like "dd-MM-yyyy" against a full timestamp (the
  // approved_on/rejected_on shape the backend sends: "16-09-2026, 14:30")
  // still "succeeds" — but silently backfills the missing hour/minute from
  // referenceDate (i.e. the current time), showing the wrong time. The
  // timestamp formats must be tried first so they win the match.
  const knownFormats = [
    "dd-MM-yyyy, HH:mm",
    "dd-MM-yyyy HH:mm",
    "dd-MM-yyyy",
    "yyyy-MM-dd",
    "dd/MM/yyyy",
    "yyyy/MM/dd",
  ];

  for (const pattern of knownFormats) {
    const parsed = parse(text, pattern, new Date());
    if (isValid(parsed)) return parsed;
  }

  const parsedIso = parseISO(text);
  return isValid(parsedIso) ? parsedIso : null;
};

const formatDisplayDate = (value) => {
  const parsed = parseKnownDate(value);
  return parsed ? format(parsed, "dd MMM yyyy") : value || "-";
};

const formatDisplayDateTime = (value) => {
  if (!value) return "-";
  const text = String(value).trim();
  const parsed =
    parseKnownDate(value) ||
    (Number.isNaN(new Date(text).getTime()) ? null : new Date(text));
  const hasTime = /[T\s]\d{1,2}:\d{2}/.test(text);

  if (!parsed) return value;
  return format(parsed, hasTime ? "dd MMM yyyy, hh:mm a" : "dd MMM yyyy");
};

const hasLeavePeriodEnded = (toDateValue) => {
  const endDate = parseKnownDate(toDateValue);
  return endDate ? isBefore(startOfDay(endDate), startOfDay(new Date())) : false;
};

const formatDayCount = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";

  const formatted = Number.isInteger(numeric) ? `${numeric}` : `${numeric}`;
  return `${formatted} ${numeric <= 1 ? "Day" : "Days"}`;
};

const formatHalfDayLabel = (selectedData) => {
  const raw =
    selectedData?.which_half ||
    selectedData?.half_day_period ||
    selectedData?.half_day_type;

  if (!raw) return "";
  const text = String(raw).trim();
  if (!text) return "";
  return /half/i.test(text) ? text : `${text} Half`;
};

const getRequestReason = (selectedData) =>
  selectedData?.applied_reason ||
  selectedData?.description ||
  selectedData?.reason ||
  "-";

export const ViewLeave = ({
  open = true,
  selectedData,
  onClose,
  leaveYearId,
  setReCallApi,
  onEdit,
  checkAccess,
}) => {
  const { userDetails } = useSelector((state) => state.tokenReducer);
  const [cancelOpen, setCancelOpen] = useState(false);
  const halfDayLabel = formatHalfDayLabel(selectedData);
  const dayLabel = formatDayCount(selectedData?.business_days);
  const periodHelper = [dayLabel !== "-" ? dayLabel : "", halfDayLabel]
    .filter(Boolean)
    .join(" / ");
  const canEdit = checkAccess && typeof onEdit === "function";
  const displayData = normalizeApprovalPersonFields({
    ...selectedData,
    employee_name:
      selectedData?.employee_name ||
      selectedData?.user_name ||
      selectedData?.username ||
      userDetails?.first_name,
    employee_id: selectedData?.employee_id || selectedData?.emp_id || userDetails?.employee_id,
  });
  const employeeName = displayData.employee_name || "N/A";
  const employeeId =
    selectedData?.employee_id ||
    selectedData?.emp_id ||
    userDetails?.employee_id ||
    "-";
  const department =
    selectedData?.department_name ||
    selectedData?.department ||
    userDetails?.department_name ||
    "-";
  const designation =
    selectedData?.designation_name ||
    selectedData?.designation ||
    userDetails?.designation ||
    "-";

  // getStatusFlow() (services/constants/index.js) expects applied_by/
  // applied_on/approved_by/approved_at — the Leave list API uses its own
  // field names (applied_date, approver_name, approved_on), so adapt here
  // rather than changing the shared util every other request type relies on.
  const statusFlowData = {
    ...selectedData,
    applied_by: displayData.applied_by || selectedData?.createdby || employeeName,
    applied_on: formatDisplayDateTime(
      selectedData?.applied_on ||
        selectedData?.createdat ||
        selectedData?.created_at ||
        selectedData?.created_date ||
        selectedData?.requested_date ||
        selectedData?.applied_date,
    ),
    applied_reason: getRequestReason(selectedData),
    approved_by: displayData.approved_by,
    approved_at: formatDisplayDateTime(
      selectedData?.approved_on || selectedData?.approved_at,
    ),
    rejected_by: displayData.rejected_by,
    rejected_at: formatDisplayDateTime(
      selectedData?.rejected_on || selectedData?.rejected_at,
    ),
    cancelled_by: displayData.cancelled_by,
    cancelled_at: formatDisplayDateTime(
      selectedData?.cancelled_on || selectedData?.cancelled_at,
    ),
  };

  // My Workspace should let the employee cancel a leave request while it is
  // still pending, using the same cancel_create_leave_request/ flow the
  // approval-side cancels use once the leave was already approved.
  const canCancel =
    checkAccess && canonicalLeaveStatus(selectedData?.status) === "Pending";

  const isLeaveCompleted =
    selectedData?.status === "Approved" && hasLeavePeriodEnded(selectedData?.to_date);

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Leave Request"
      subtitle="Request details and status"
      headerSx={{ px: 2.5, pt: 2.5, pb: 1.5 }}
      footer={
        canCancel || canEdit ? (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ width: "100%" }}
          >
            {canEdit ? (
              <SecondaryButton
                onClick={onEdit}
                startIcon={<EditOutlinedIcon sx={{ fontSize: 17 }} />}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 42,
                  color: "#4F46E5",
                  borderColor: "#C7D2FE",
                  "&:hover": {
                    color: "#4338CA",
                    borderColor: "#A5B4FC",
                    backgroundColor: "#EEF2FF",
                  },
                }}
              >
                Edit Request
              </SecondaryButton>
            ) : null}
            {canCancel ? (
              <RequestCancelButton
                onClick={() => setCancelOpen(true)}
                sx={{ flex: 1 }}
              />
            ) : null}
          </Stack>
        ) : null
      }
    >
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        <RequestDetailHeader
          variant="employeeDetailsCard"
          userDetails={{
            image_url: selectedData?.image_url || userDetails?.image_url,
            employee_id: employeeId,
            user_name: employeeName,
            department_name: department,
            designation,
          }}
          status={selectedData?.status}
          colorCode={selectedData?.leave_color || selectedData?.color_code}
        />

        <RequestDetailsSection
          fields={[
            {
              icon: CalendarMonth,
              label: "Leave Period",
              value: selectedData?.from_date
                ? `${formatDisplayDate(selectedData.from_date)} - ${formatDisplayDate(
                    selectedData?.to_date,
                  )}`
                : "-",
              helper: periodHelper,
              accent: detailAccent.period,
            },
            {
              icon: AccountBalanceWalletOutlinedIcon,
              label: "Leave Type",
              value: selectedData?.leave_type,
              accent: detailAccent.type,
            },
            {
              icon: EventAvailableOutlinedIcon,
              label: "Business Days",
              value: dayLabel,
              rightPill: halfDayLabel,
              accent: detailAccent.days,
            },
            {
              icon: DescriptionOutlinedIcon,
              label: "Reason",
              value: getRequestReason(selectedData),
              accent: detailAccent.reason,
              multiline: true,
            },
            ...(selectedData?.is_sandwich_applied && selectedData?.comments
              ? [
                  {
                    icon: DescriptionOutlinedIcon,
                    label: "Sandwich Policy",
                    value: selectedData.comments,
                    accent: detailAccent.policy,
                    multiline: true,
                  },
                ]
              : []),
          ]}
        />

        <RequestStatusTimeline
          data={statusFlowData}
          variant="stepper"
          forceCompleted={isLeaveCompleted}
          hidePendingStep
        />

        {selectedData?.status === "Approved" && !isLeaveCompleted ? (
          <RequestInfoBanner
            title="This request will be marked as Completed after the leave period ends."
            body="For single day leave, it will be completed at the end of that day."
          />
        ) : null}

      </Box>

      <CancelLeaveDialog
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        cancelLeaveData={{
          ...selectedData,
          is_half_day: selectedData?.half_day,
        }}
        requireEditorPermission={false}
        leaveYearId={leaveYearId}
        onCancelled={() => {
          setCancelOpen(false);
          setReCallApi?.(true);
          onClose?.();
        }}
      />
    </GlobalSidePanel>
  );
};
